import type {
  IntegrationCrmSettings,
  IntegrationItem,
  IntegrationStatus,
  VkontakteSettings,
} from '@/api/integrations/integrations.types'
import rolesEnum from '@/constant/rolesEnum'
import { buildVkClubUrl } from '../../VkIntegrationConfigForm/lib/vkIntegrationForm.helpers'

export const VK_CRM_USAGE = 'crm' as const

export const VK_CRM_STATUS_LABELS: Record<IntegrationStatus, string> = {
  draft: 'Черновик',
  connected: 'Подключено',
  disabled: 'Отключено',
  error: 'Ошибка',
}

export const isVkCrmIntegration = (item: IntegrationItem): boolean => {
  const usages = item.usages
  // Миграция со старого VK: draft без usages / без crm — всё равно показываем
  if (!Array.isArray(usages) || usages.length === 0) return true
  if (usages.includes(VK_CRM_USAGE)) return true
  const notificationOnly = usages.every(
    (usage) =>
      usage === 'notifications' ||
      usage === 'password_reset' ||
      usage === 'mailing',
  )
  return !notificationOnly
}

export const filterVkCrmIntegrations = (
  items: IntegrationItem[] | null | undefined,
): IntegrationItem[] => {
  if (!Array.isArray(items)) return []
  return items.filter(isVkCrmIntegration)
}

export const getVkIntegrationGroupId = (
  item: IntegrationItem | null | undefined,
): string => {
  if (!item) return ''
  const fromCredentials = item.credentials?.group_id
  const fromSettings = (item.settings as VkontakteSettings | undefined)?.group_id
  const raw = fromCredentials ?? fromSettings
  if (raw == null || raw === '') return ''
  return String(raw).trim()
}

export const getVkIntegrationClubUrl = (
  item: IntegrationItem | null | undefined,
): string | null => buildVkClubUrl(getVkIntegrationGroupId(item))

export const MASKED_SECRET_PLACEHOLDER = '********'

export const isMaskedSecret = (value: string): boolean => {
  const trimmed = value.trim()
  return trimmed.length > 0 && /^[*•]+$/.test(trimmed)
}

/** Значение секрета для инпута: маска с бэка → ровно `********`. */
export const getCredentialInputValue = (
  credentials: Record<string, string> | null | undefined,
  key: string,
): string => {
  const raw = credentials?.[key]
  if (typeof raw !== 'string') return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return isMaskedSecret(trimmed) ? MASKED_SECRET_PLACEHOLDER : trimmed
}

/** Дефолты CRM; funnel / lead_fields / access — задел на следующий шаг */
export const createDefaultVkCrmSettings = (
  overrides?: Partial<IntegrationCrmSettings>,
): IntegrationCrmSettings => ({
  funnel_id: null,
  traffic_channel_id: null,
  create_lead: true,
  create_chat: true,
  lead_fields: [],
  access: {
    roles: [rolesEnum.admin],
    departments: [],
  },
  ...overrides,
})

export const normalizeIntegrationTrafficChannelId = (
  value: number | null | undefined,
): number | null => {
  if (value == null || value === ('' as unknown as number)) {
    return null
  }
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) {
    return null
  }
  return Math.floor(num)
}

export const normalizeIntegrationFunnelId = (
  value: number | null | undefined,
): number | null => {
  if (value == null || value === ('' as unknown as number)) {
    return null
  }
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) {
    return null
  }
  return Math.floor(num)
}

export const readVkCrmSettings = (
  item: IntegrationItem | null | undefined,
): IntegrationCrmSettings => {
  const crm = (item?.settings as VkontakteSettings | undefined)?.crm
  return createDefaultVkCrmSettings({
    funnel_id: normalizeIntegrationFunnelId(crm?.funnel_id),
    traffic_channel_id: normalizeIntegrationTrafficChannelId(
      crm?.traffic_channel_id,
    ),
    create_lead: crm?.create_lead ?? true,
    create_chat: crm?.create_chat ?? true,
    lead_fields:
      crm?.lead_fields && Array.isArray(crm.lead_fields) ? crm.lead_fields : [],
    access: {
      roles: crm?.access?.roles ?? [],
      departments: crm?.access?.departments ?? [],
    },
  })
}

/** Мержит UI-поля, остальное settings.crm не затирает */
export const mergeVkCrmSettingsForSave = (
  existing: IntegrationCrmSettings | undefined,
  patch: Pick<
    IntegrationCrmSettings,
    | 'create_lead'
    | 'create_chat'
    | 'access'
    | 'traffic_channel_id'
    | 'funnel_id'
  >,
): IntegrationCrmSettings => {
  const base = createDefaultVkCrmSettings(existing)
  return {
    ...base,
    ...existing,
    create_lead: patch.create_lead,
    create_chat: patch.create_chat,
    funnel_id: normalizeIntegrationFunnelId(patch.funnel_id),
    traffic_channel_id: normalizeIntegrationTrafficChannelId(
      patch.traffic_channel_id,
    ),
    lead_fields: existing?.lead_fields ?? base.lead_fields,
    access: {
      roles: patch.access?.roles ?? existing?.access?.roles ?? base.access?.roles ?? [],
      departments:
        patch.access?.departments ??
        existing?.access?.departments ??
        base.access?.departments ??
        [],
    },
  }
}

export const canConnectVkCrmIntegration = (status: IntegrationStatus): boolean =>
  status === 'draft' || status === 'error' || status === 'disabled'

export const getIntegrationsListErrorText = (
  error: unknown,
  fallback: string,
): string => {
  const responseData = (error as { response?: { data?: any } })?.response?.data
  const fromData = responseData?.data
  if (typeof fromData === 'string' && fromData.trim()) return fromData
  const errorList = responseData?.error
  if (Array.isArray(errorList) && errorList.length > 0) {
    const texts = errorList
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item && typeof item === 'object') {
          const message =
            (item as { message?: unknown }).message ??
            (item as { text?: unknown }).text
          return typeof message === 'string' ? message.trim() : ''
        }
        return ''
      })
      .filter(Boolean)
    if (texts.length > 0) return texts.join('; ')
  }
  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message
  }
  return fallback
}
